<script setup lang="ts">
  import { ref, computed, onMounted, watch } from 'vue';
  import { SearchCombobox, FilterModal, ThemeProvider } from '@/shared/ui';
  import { useDepartmentOptions } from '../../lib';

  interface PositionFilters {
    departmentId?: number;
  }

  const props = defineProps<{
    currentFilters?: PositionFilters;
  }>();

  const emit = defineEmits<{
    close: [];
    'update:filter': [filters: PositionFilters];
    'clear-filters': [];
  }>();

  const initialDeptId = computed(() => props.currentFilters?.departmentId);

  const { departmentOptions, isLoading, loadDepartments, handleSearch, initialDepartmentLabel } =
    useDepartmentOptions({ initialDepartmentId: initialDeptId });

  const formData = ref({
    departmentId: '',
  });

  function loadFilters(filters?: PositionFilters) {
    if (!filters) return;

    formData.value.departmentId = filters.departmentId ? String(filters.departmentId) : '';
  }

  onMounted(async () => {
    await loadDepartments();
    loadFilters(props.currentFilters);
  });

  watch(
    () => props.currentFilters,
    (newFilters) => {
      if (newFilters) {
        loadFilters(newFilters);
      }
    },
    { deep: true }
  );

  const handleApplyFilters = () => {
    const filters: PositionFilters = {};

    if (formData.value.departmentId) {
      filters.departmentId = Number(formData.value.departmentId);
    }

    emit('update:filter', filters);
  };

  const handleClearFilters = () => {
    formData.value.departmentId = '';
    emit('update:filter', {});
  };
</script>

<template>
  <FilterModal
    content-class="w-auto max-w-md"
    :buttons-text="{ clear: 'Limpar filtro', apply: 'Aplicar filtro' }"
    @close="emit('close')"
    @apply-filters="handleApplyFilters"
    @clear-filters="handleClearFilters"
  >
    <template #content>
      <ThemeProvider>
        <div class="p-3">
          <div class="p-6">
            <h4 class="font-medium text-gray-07 pb-4">
              Departamento
            </h4>
            <SearchCombobox
              :model-value="formData.departmentId"
              :options="departmentOptions"
              :loading="isLoading"
              :initial-label="initialDepartmentLabel"
              placeholder="Selecione um Departamento"
              search-placeholder="Buscar departamento..."
              class="w-full min-w-70"
              @update:model-value="formData.departmentId = $event || ''"
              @search="handleSearch"
            />
          </div>
        </div>
      </ThemeProvider>
    </template>
  </FilterModal>
</template>
